import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextInput,
  Heading,
  Layer,
  Text,
  TextArea,
  FormField,
  CheckBox,
} from "grommet";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import RoomTypeService from "../../services/room-type.service";
import PhotoService from "../../services/photo.service";
import { PhotoUploader } from "./";
import { PriceInput } from "@shared/";
import { useFetchHotel } from "./redux/hooks";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import { useTranslation } from "react-i18next";

const defaultValues = {
  _id: 0,
  name: "",
  description: "",
  upgradePrice: "",
  earlyCheckinPrice: "",
  lateCheckoutPrice: "",
  sleeps: 2,
  surface: 0,
  noRoomLabel: false,
};

export default function AddRoomType(props) {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const upsellsEnabled = getOfferCapabilities(hotel).upsells;
  const { roomTypeId, onClose } = props;
  const [roomType, setRoomType] = useState(defaultValues);
  const [photo, setPhoto] = useState(undefined);

  function handleInputChange({ target: { name, value } }) {
    var values = { ...roomType };
    values[name] = value;
    setRoomType(values);
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    setRoomType({ ...roomType, [name]: checked });
  }

  const handleAdd = () => {
    const update = { ...roomType };
    if (!upsellsEnabled) ["upgradePrice", "earlyCheckinPrice", "lateCheckoutPrice"].forEach(key => delete update[key]);
    if (roomTypeId === 0) {
      RoomTypeService.addRoomType(update).then(
        async (response) => {
          const roomType = response.data && response.data.roomType;
          if (roomType) await uploadPhoto(roomType._id);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        }
      );
    } else {
      RoomTypeService.updateRoomType(update).then(
        async (response) => {
          await uploadPhoto(roomTypeId);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        }
      );
    }
  };

  const uploadPhoto = async (typeId) => {
    if (!photo) return;
    await RoomTypeService.uploadPhoto(typeId, photo).then(
      (response) => {
        if (response.data.success) {
          //presentToastSuccess("Photo Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      }
    );
  };

  useEffect(() => {
    if (roomTypeId !== 0) {
      presentLoading();
      RoomTypeService.getRoomType(roomTypeId).then(
        (response) => {
          dismissLoading();
          const roomType = response.data;
          if (roomType._id) {
            setRoomType(roomType);
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        }
      );
    } else {
      setRoomType(defaultValues);
    }
  }, [roomTypeId]);

  return (
    <Layer position="center" onClickOutside={onClose} onEsc={onClose}>
      <Box pad="medium" gap="small" width="large">
        <Heading level={3} margin="none">
          {roomTypeId === 0
            ? t("roomTypeDialog.addTitle")
            : t("roomTypeDialog.editTitle")}
        </Heading>
        <Box direction="row" gap="medium">
          <Box>
            <FormField label={t("roomTypeDialog.name")} width="medium">
              <TextInput
                name="name"
                value={roomType.name}
                onChange={handleInputChange}
              />
            </FormField>
            <CheckBox
              name={"noRoomLabel"}
              checked={roomType.noRoomLabel}
              onChange={handleCheckboxChange}
              label={t("roomTypeDialog.noRoomLabel")}
            />
          </Box>
          <Box>
            <Text>{t("roomTypeDialog.photo")}</Text>
            <PhotoUploader
              label={t("roomTypeDialog.photoUpload")}
              fitWidth={980}
              fitHeight={600}
              onChange={setPhoto}
              photo={PhotoService.getURL(roomType.photo) || ""}
            />
          </Box>
        </Box>
        <Text>{t("roomTypeDialog.description")}</Text>
        <TextArea
          style={{ height: 100 }}
          name="description"
          value={roomType.description}
          onChange={handleInputChange}
        />
        <Box direction="row" gap="medium">
          <FormField label={t("roomTypeDialog.surface")} fill>
            <TextInput
              value={roomType.surface}
              name="surface"
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("roomTypeDialog.sleeps")} fill>
            <TextInput
              value={roomType.sleeps}
              name="sleeps"
              onChange={handleInputChange}
            />
          </FormField>
        </Box>
        {upsellsEnabled && <Box direction="row" gap="medium">
          <FormField label={t("roomTypeDialog.upgradePrice")} fill>
            <PriceInput
              style={{ border: 0 }}
              value={roomType.upgradePrice}
              name="upgradePrice"
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("roomTypeDialog.earlyCheckinPrice")} fill>
            <PriceInput
              style={{ border: 0 }}
              value={roomType.earlyCheckinPrice}
              name="earlyCheckinPrice"
              onChange={handleInputChange}
            />
          </FormField>
          <FormField label={t("roomTypeDialog.lateCheckoutPrice")} fill>
            <PriceInput
              style={{ border: 0 }}
              value={roomType.lateCheckoutPrice}
              name="lateCheckoutPrice"
              onChange={handleInputChange}
            />
          </FormField>
        </Box>}
        <Box
          as="footer"
          gap="small"
          direction="row"
          align="center"
          justify="end"
          pad={{ top: "medium", bottom: "small" }}
        >
          <Button label={t("common.cancel")} onClick={onClose} color="dark-3" />
          <Button
            label={roomTypeId === 0 ? t("common.add") : t("common.update")}
            onClick={handleAdd}
            primary
            color="status-ok"
          />
        </Box>
      </Box>
    </Layer>
  );
}

AddRoomType.propTypes = {};
AddRoomType.defaultProps = {};
